/**
 * Deutsche Beschriftungen und Gruppierung der Rechte-Bits aus `Permissions` (shared) für Rollen- und
 * Kanal-Override-Editoren. Jedes Bit muss hier genau einmal vorkommen (Test in permissionCatalog.test.ts).
 */
import type { ChannelType, PermissionName } from '@lagerfeuer/shared';

export interface PermissionInfo {
  name: PermissionName;
  label: string;
  description: string;
  /** Nur auf Serverebene sinnvoll (nicht als Kanal-Override). */
  serverOnly?: boolean;
  /** Nur in Sprachkanälen/Kategorien relevant. */
  voice?: boolean;
}

export interface PermissionGroup {
  id: string;
  label: string;
  items: PermissionInfo[];
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    id: 'allgemein',
    label: 'Allgemeine Serverrechte',
    items: [
      { name: 'VIEW_CHANNEL', label: 'Kanäle ansehen', description: 'Erlaubt, Kanäle zu sehen und darin zu lesen (sofern nicht verweigert).' },
      { name: 'MANAGE_CHANNELS', label: 'Kanäle verwalten', description: 'Kanäle und Kategorien erstellen, bearbeiten, sortieren und löschen.' },
      { name: 'MANAGE_ROLES', label: 'Rollen verwalten', description: 'Rollen unterhalb der eigenen höchsten Rolle erstellen, bearbeiten und vergeben; Kanalrechte anpassen.' },
      { name: 'MANAGE_EMOJIS', label: 'Ausdrücke verwalten', description: 'Eigene Emojis, Sticker und Sounds hochladen, umbenennen und löschen.', serverOnly: true },
      { name: 'VIEW_AUDIT_LOG', label: 'Audit-Log ansehen', description: 'Protokoll der Moderations- und Verwaltungsaktionen ansehen.', serverOnly: true },
      { name: 'MANAGE_WEBHOOKS', label: 'Webhooks verwalten', description: 'Webhooks erstellen, bearbeiten und löschen.' },
      { name: 'MANAGE_SERVER', label: 'Server verwalten', description: 'Name, Icon, Banner, Vanity-URL und Einladungen des Servers verwalten.', serverOnly: true },
    ],
  },
  {
    id: 'mitglieder',
    label: 'Mitgliedschaft',
    items: [
      { name: 'CREATE_INVITE', label: 'Einladungen erstellen', description: 'Neue Leute in den Server einladen.' },
      { name: 'CHANGE_NICKNAME', label: 'Eigenen Spitznamen ändern', description: 'Den eigenen Spitznamen auf diesem Server ändern.', serverOnly: true },
      { name: 'MANAGE_NICKNAMES', label: 'Spitznamen verwalten', description: 'Spitznamen anderer Mitglieder ändern.', serverOnly: true },
      { name: 'KICK_MEMBERS', label: 'Mitglieder entfernen', description: 'Mitglieder mit niedrigerer Rolle aus dem Server werfen.', serverOnly: true },
      { name: 'BAN_MEMBERS', label: 'Mitglieder bannen', description: 'Mitglieder mit niedrigerer Rolle dauerhaft ausschließen und Banns aufheben.', serverOnly: true },
      { name: 'MODERATE_MEMBERS', label: 'Timeout vergeben', description: 'Mitglieder vorübergehend stummschalten (Timeout, höchstens 28 Tage).', serverOnly: true },
    ],
  },
  {
    id: 'text',
    label: 'Textkanäle',
    items: [
      { name: 'SEND_MESSAGES', label: 'Nachrichten senden', description: 'Nachrichten in Textkanälen senden und Forenbeiträge erstellen.' },
      { name: 'SEND_IN_THREADS', label: 'In Threads schreiben', description: 'Nachrichten in Threads und Forenbeiträgen senden.' },
      { name: 'CREATE_THREADS', label: 'Öffentliche Threads erstellen', description: 'Threads und Forenbeiträge starten, die alle sehen können.' },
      { name: 'CREATE_PRIVATE_THREADS', label: 'Private Threads erstellen', description: 'Threads nur für eingeladene Mitglieder starten.' },
      { name: 'EMBED_LINKS', label: 'Links einbetten', description: 'Vorschauen für gesendete Links anzeigen.' },
      { name: 'ATTACH_FILES', label: 'Dateien anhängen', description: 'Dateien und Medien hochladen.' },
      { name: 'ADD_REACTIONS', label: 'Reaktionen hinzufügen', description: 'Neue Reaktionen an Nachrichten setzen.' },
      { name: 'USE_SUPER_REACTIONS', label: 'Super-Reaktionen verwenden', description: 'Animierte Super-Reaktionen vergeben.' },
      { name: 'USE_EXTERNAL_EMOJIS', label: 'Externe Emojis verwenden', description: 'Eigene Emojis anderer Server verwenden.' },
      { name: 'USE_EXTERNAL_STICKERS', label: 'Externe Sticker verwenden', description: 'Sticker anderer Server verwenden.' },
      { name: 'MENTION_EVERYONE', label: '@everyone, @here und alle Rollen erwähnen', description: 'Alle Mitglieder bzw. nicht erwähnbare Rollen benachrichtigen.' },
      { name: 'MANAGE_MESSAGES', label: 'Nachrichten verwalten', description: 'Nachrichten anderer löschen und Einbettungen entfernen.' },
      { name: 'PIN_MESSAGES', label: 'Nachrichten anpinnen', description: 'Nachrichten im Kanal anheften und lösen.' },
      { name: 'MANAGE_THREADS', label: 'Threads verwalten', description: 'Threads umbenennen, archivieren, sperren und löschen.' },
      { name: 'READ_MESSAGE_HISTORY', label: 'Nachrichtenverlauf lesen', description: 'Frühere Nachrichten im Kanal lesen.' },
    ],
  },
  {
    id: 'sprache',
    label: 'Sprachkanäle',
    items: [
      { name: 'CONNECT', label: 'Verbinden', description: 'Sprachkanälen beitreten.', voice: true },
      { name: 'SPEAK', label: 'Sprechen', description: 'In Sprachkanälen sprechen.', voice: true },
      { name: 'VIDEO', label: 'Kamera', description: 'Die eigene Kamera übertragen.', voice: true },
      { name: 'STREAM', label: 'Bildschirm übertragen', description: 'Bildschirm oder Fenster teilen.', voice: true },
      { name: 'USE_VAD', label: 'Sprachaktivierung verwenden', description: 'Ohne Push-to-Talk sprechen.', voice: true },
      { name: 'PRIORITY_SPEAKER', label: 'Vorrangiger Sprecher', description: 'Andere werden leiser, wenn du sprichst.', voice: true },
      { name: 'USE_SOUNDBOARD', label: 'Soundboard verwenden', description: 'Sounds im Sprachkanal abspielen.', voice: true },
      { name: 'USE_EXTERNAL_SOUNDS', label: 'Externe Sounds verwenden', description: 'Sounds anderer Server abspielen.', voice: true },
      { name: 'MUTE_MEMBERS', label: 'Mitglieder stummschalten', description: 'Andere im Sprachkanal serverseitig stummschalten.', voice: true },
      { name: 'DEAFEN_MEMBERS', label: 'Mitglieder taub schalten', description: 'Anderen im Sprachkanal den Ton abschalten.', voice: true },
      { name: 'MOVE_MEMBERS', label: 'Mitglieder verschieben', description: 'Mitglieder zwischen Sprachkanälen verschieben oder trennen.', voice: true },
    ],
  },
  {
    id: 'erweitert',
    label: 'Erweitert',
    items: [
      {
        name: 'ADMINISTRATOR',
        label: 'Administrator',
        description: 'Alle Rechte und Umgehung aller Kanal-Overrides. Nur sehr vertrauenswürdigen Rollen geben!',
        serverOnly: true,
      },
    ],
  },
];

export const PERMISSION_INFO: Record<PermissionName, PermissionInfo> = Object.fromEntries(
  PERMISSION_GROUPS.flatMap((g) => g.items).map((i) => [i.name, i]),
) as Record<PermissionName, PermissionInfo>;

/** Gruppen für den Override-Editor eines Kanals (ohne reine Serverrechte; Sprachrechte nur bei Sprache/Kategorie). */
export function channelPermissionGroups(type: ChannelType): PermissionGroup[] {
  const voiceOk = type === 'VOICE' || type === 'CATEGORY';
  return PERMISSION_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => !i.serverOnly && (voiceOk || !i.voice)) })).filter((g) => g.items.length > 0);
}
